/**
 * <variant-upsell-v2> custom element for the V2 PDP metaobject-driven
 * variant upsell cards (see snippets rendering `.vu-v2__card` /
 * `[data-vu-card]` with an embedded `[data-vu-variants]` JSON blob and
 * auto-derived option selectors — Tasks 1-3 of this feature).
 *
 * Scoped per-instance: every query below is scoped to `this` (the
 * <variant-upsell-v2> wrapper), so multiple upsell blocks can coexist on
 * the same page without cross-talk.
 *
 * Responsibilities:
 *
 * 1. Selector resolution (data-vu-selector / data-vu-input radios) — on
 *    change, reads the checked value per option position (1/2/3) from
 *    the card's selector groups, finds the matching variant in the
 *    card's `[data-vu-variants]` JSON, and updates that card's price
 *    (data-vu-price, sale + optional compare-at), image
 *    (data-vu-image img — src swap after removing srcset/sizes so the
 *    browser doesn't keep rendering from the stale srcset), and ADD
 *    button's data-variant-id. Also updates the selected swatch/tile
 *    visual state and the `[data-vu-optlabel]` text. No server
 *    round-trip: the product's variants are already fully present in
 *    the DOM via the per-card JSON, mirroring
 *    assets/component-living-room-upsell-v2.js's shape/colour toggles.
 *
 * 2. ADD buttons (data-vu-add) — POST a single-item payload to
 *    /cart/add.js and refresh the cart drawer, reusing the exact
 *    sections/renderContents pattern from
 *    assets/component-living-room-upsell-v2.js: same
 *    fetchConfig('javascript') call, same cart.getSectionsToRender() +
 *    sections_url body fields, same cart.renderContents(response)
 *    refresh / cart.setActiveElement focus-return call, same
 *    PUB_SUB_EVENTS.cartUpdate publish. Falls back to a full-page
 *    navigation to the cart when no cart-drawer/cart-notification
 *    element is present, matching that file's own fallback.
 *
 * Note: `card.__vuMainVal` / `data-vu-match-option` referenced in
 * `_match` are hooks for Task 5's match_main live filtering and are not
 * yet populated by anything in this file.
 */
(function () {
  if (window.__v2VariantUpsellBound) return;
  window.__v2VariantUpsellBound = true;
  if (customElements.get('variant-upsell-v2')) return;

  // Byte-consistent duplication of the 3-tier money formatter from
  // assets/component-living-room-upsell-v2.js (see that file's doc
  // comment for the full precedence rationale: Shopify.formatMoney with
  // the shop's real money_format string first, then a local
  // money_format interpreter, then window.formatMoney as a last resort).
  function formatWithDelimiters(cents, precision, thousands, decimal) {
    if (isNaN(cents) || cents == null) return '0';
    var number = (cents / 100.0).toFixed(precision);
    var parts = number.split('.');
    var dollars = parts[0].replace(
      /(\d)(?=(\d\d\d)+(?!\d))/g,
      '$1' + thousands,
    );
    var centsPart = '';
    if (parts[1]) centsPart = decimal + parts[1];
    return dollars + centsPart;
  }

  function formatWithMoneyFormat(cents, format) {
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var match = format.match(placeholderRegex);
    var value;
    var placeholder = 'amount';
    if (match) placeholder = match[1];
    switch (placeholder) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      default:
        value = formatWithDelimiters(cents, 2, ',', '.');
    }
    return format
      .replace(placeholderRegex, value)
      .replace(/<[^>]*>/g, '')
      .trim();
  }

  function formatMoneyCents(cents, format) {
    if (format) {
      if (
        typeof Shopify !== 'undefined' &&
        typeof Shopify.formatMoney === 'function'
      ) {
        return Shopify.formatMoney(cents, format);
      }
      return formatWithMoneyFormat(cents, format);
    }
    if (typeof window.formatMoney === 'function') {
      return '$' + window.formatMoney(cents);
    }
    return '$' + (Math.round(cents) / 100).toFixed(0);
  }

  // Shared reservation registry (parallel to window.V2Addons — see
  // component-modules-addons-v2.js). In the deferred flow a card's selected
  // upsell is "reserved" on card.__vuReserved = { variantId, qty, priceCents }
  // rather than added to the cart; <buy-buttons-v2> reads getItems()/getTotal()
  // to fold the reserved upsells into the main ATC label and the bundled submit,
  // and calls reset() after a successful add. Scans the DOM so a single registry
  // covers every card across every <variant-upsell-v2> on the page.
  function reservedCards() {
    return Array.prototype.filter.call(
      document.querySelectorAll('[data-vu-card]'),
      function (c) {
        return c.__vuReserved && c.__vuReserved.qty > 0;
      },
    );
  }
  window.V2Upsell = window.V2Upsell || {
    getItems: function () {
      return reservedCards().map(function (c) {
        return { variantId: c.__vuReserved.variantId, qty: c.__vuReserved.qty };
      });
    },
    getTotal: function () {
      return reservedCards().reduce(function (sum, c) {
        return sum + c.__vuReserved.priceCents * c.__vuReserved.qty;
      }, 0);
    },
    reset: function () {
      reservedCards().forEach(function (c) {
        c.__vuReserved = null;
        var add = c.querySelector('[data-vu-add]');
        var stepper = c.querySelector('[data-vu-stepper]');
        if (add) add.hidden = false;
        if (stepper) stepper.hidden = true;
      });
      dispatchUpsellChange();
    },
  };
  function dispatchUpsellChange() {
    document.dispatchEvent(
      new CustomEvent('upsell:change', {
        detail: {
          items: window.V2Upsell.getItems(),
          totalPrice: window.V2Upsell.getTotal(),
        },
      }),
    );
  }

  // --- Main-product option matching ---------------------------------------
  // The main product's currently-selected value for the option whose name passes
  // `matches(name)`, read from its V2 option pickers (data-v2-option-input /
  // data-v2-selected-label carry data-option-name) or V1 pickers (variant-radios
  // / variant-selects, where the input/select `name` IS the option name). Returns
  // null when the product has no such option or none is selected.
  function mainOptionValue(matches) {
    // V2 pickers (arm-type / config / fabric) — data-option-name + data-option-value.
    var checked = document.querySelectorAll('[data-v2-option-input]:checked');
    for (var i = 0; i < checked.length; i++) {
      var name = (checked[i].dataset.optionName || '').toLowerCase();
      if (matches(name)) return checked[i].dataset.optionValue;
    }
    var labels = document.querySelectorAll('[data-v2-selected-label][data-option-name]');
    for (var k = 0; k < labels.length; k++) {
      var n = (labels[k].dataset.optionName || '').toLowerCase();
      if (matches(n)) return labels[k].textContent.trim();
    }
    // V1 picker (variant-radios / variant-selects). Scoped to the variant picker
    // so the upsell's own [data-vu-input] radios are never matched.
    var v1radios = document.querySelectorAll(
      'variant-radios input[type="radio"]:checked, variant-selects input[type="radio"]:checked',
    );
    for (var r = 0; r < v1radios.length; r++) {
      if (matches((v1radios[r].name || '').toLowerCase())) return v1radios[r].value;
    }
    var v1selects = document.querySelectorAll('variant-radios select, variant-selects select');
    for (var s = 0; s < v1selects.length; s++) {
      if (matches((v1selects[s].name || '').toLowerCase())) return v1selects[s].value;
    }
    // No picker on the page for this option (e.g. a single-value "Wood Finish",
    // or its picker block disabled): fall back to the main product's selected
    // variant value, rendered by variant-upsell-v2.liquid as data-vu-main-options.
    var fixed = mainFixedOptions();
    for (var key in fixed) {
      if (matches(key.toLowerCase())) return fixed[key];
    }
    return null;
  }

  // { optionName: selectedValue } for the main product, parsed once.
  var mainFixedCache = null;
  function mainFixedOptions() {
    if (mainFixedCache) return mainFixedCache;
    var el = document.querySelector('[data-vu-main-options]');
    try {
      mainFixedCache = el ? JSON.parse(el.dataset.vuMainOptions) : {};
    } catch (e) {
      mainFixedCache = {};
    }
    return mainFixedCache;
  }

  function nameHas(word) {
    return function (n) {
      return n.indexOf(word) !== -1;
    };
  }

  // Upsell options that FOLLOW the main product (locked to its selection).
  // `card` matches the upsell's option name; `main` lists main-product option
  // matchers tried in order. A sofa's "Configuration" is the cover's "Size"
  // (e.g. Everyday Sofa -> Everyday Sofa Covers); "Arm Type" and "Wood Finish"
  // (Timber Bed Frame -> Side Table) map 1:1.
  var LOCKED_OPTIONS = [
    { card: nameHas('size'), main: [nameHas('size'), nameHas('configuration')] },
    { card: nameHas('arm'), main: [nameHas('arm')] },
    { card: nameHas('finish'), main: [nameHas('finish')] },
  ];

  function mainLockedValue(rule) {
    for (var i = 0; i < rule.main.length; i++) {
      var val = mainOptionValue(rule.main[i]);
      if (val) return val;
    }
    return null;
  }

  function mainColourValue() {
    return mainOptionValue(function (n) {
      return n.indexOf('colour') !== -1 || n.indexOf('color') !== -1;
    });
  }

  // Snap every upsell card's locked selectors (Size / Arm Type — see
  // LOCKED_OPTIONS) to the main product's value, when that value exists in the
  // card. Dispatches a native change so the owning <variant-upsell-v2> updates
  // the card's price/image (and re-points any reservation). Cards without the
  // value keep their current selection.
  function syncLockedToMain() {
    var mainVals = LOCKED_OPTIONS.map(mainLockedValue);
    document.querySelectorAll('[data-vu-card] [data-vu-selector]').forEach(function (grp) {
      var oname = (grp.dataset.vuOptionName || '').toLowerCase();
      var idx = -1;
      for (var r = 0; r < LOCKED_OPTIONS.length; r++) {
        if (LOCKED_OPTIONS[r].card(oname)) {
          idx = r;
          break;
        }
      }
      if (idx === -1) return;
      var want = mainVals[idx];
      // No main value to follow (e.g. a sofa PDP with no "Size" for a coffee-table
      // upsell) — leave the picker fully interactive for the shopper.
      if (!want) {
        grp.classList.remove('vu-v2__selector--locked');
        return;
      }
      want = want.trim().toLowerCase();
      var target = null;
      grp.querySelectorAll('[data-vu-input]').forEach(function (inp) {
        if ((inp.value || '').trim().toLowerCase() === want) target = inp;
      });
      if (target && !target.checked) {
        target.checked = true;
        target.dispatchEvent(new Event('change', { bubbles: true }));
      }
      // The main product drives this option — lock the selector: hide the
      // pickable tiles but keep the "Size: X" label (which follows the main). If
      // the upsell doesn't carry the main's value (e.g. Hideaway Coffee Table
      // "Long"/"Square" vs a sofa Configuration), leave it interactive.
      grp.classList.toggle('vu-v2__selector--locked', !!target);
    });
  }

  // Pre-select every colour swatch selector to the main product's chosen colour,
  // when the card carries that colour — so most shoppers never need to open the
  // overflow grid. Unlike Size, the selector is NOT locked: colour stays
  // pickable. Values come from the same option set on both sides, so a
  // case-insensitive compare is exact. Cards without the colour keep their pick.
  function syncColourToMain() {
    var colour = mainColourValue();
    if (!colour) return;
    var want = colour.trim().toLowerCase();
    document
      .querySelectorAll('[data-vu-card] [data-vu-selector][data-vu-kind="swatch"]')
      .forEach(function (grp) {
        var target = null;
        grp.querySelectorAll('[data-vu-input]').forEach(function (inp) {
          if ((inp.value || '').trim().toLowerCase() === want) target = inp;
        });
        if (target && !target.checked) {
          target.checked = true;
          // Bubbles to the owning <variant-upsell-v2>._onChange, which updates the
          // ring, "Colour: X" label, price, image and any reservation.
          target.dispatchEvent(new Event('change', { bubbles: true }));
        }
        // Keep the (possibly newly) selected swatch inside the visible row.
        var wrap = grp.querySelector('[data-vu-swatch-wrap]');
        if (wrap && wrap.__vuVisible && !wrap.classList.contains('lru-v2__swatches--expanded')) {
          applyCollapsed(wrap);
        }
      });
  }

  // --- Colour swatch overflow (single row + "+N" chip -> expand grid) --------
  // How many swatches show in the collapsed row before the chip. The merchant sets
  // the DESKTOP cap via the block's "Colours shown before +N" range
  // (data-vu-swatch-visible; 6 default), but we never show more than physically
  // fit one row at the current width — otherwise the nowrap row overflows its
  // (narrow) column on mobile. So visible = min(configured, whatFitsWithChip).
  // Returns the configured cap unmeasured when the wrap isn't laid out yet (e.g. a
  // card inside a still-closed <dialog> drawer, clientWidth 0).
  function swatchVisibleCount(wrap) {
    var configured = parseInt(wrap.dataset.vuSwatchVisible, 10) || 6;
    var total = wrap.querySelectorAll('.lru-v2__swatch').length;
    // A chip is needed if the merchant cap is exceeded (> configured) OR the row is
    // too narrow to fit them all. Returning >= total means "no chip" to the caller.
    var capNeedsChip = total > configured;
    var wrapW = wrap.clientWidth;
    var sample = wrap.querySelector('.lru-v2__swatch');
    if (!wrapW || !sample) {
      // Unmeasured (e.g. closed drawer): honour the cap only.
      return capNeedsChip ? configured : total;
    }
    var pitch = sample.offsetWidth + (parseFloat(getComputedStyle(wrap).columnGap || getComputedStyle(wrap).gap) || 0);
    var fits = pitch > 0 ? Math.floor(wrapW / pitch) : total;
    var widthNeedsChip = total > fits;
    if (!capNeedsChip && !widthNeedsChip) return total; // all fit, cap not exceeded — no chip
    // Chip needed: show as many as fit (reserving its slot), never above the cap.
    return Math.min(configured, Math.max(1, fits - 1));
  }

  // If the checked swatch sits beyond the visible slots, move it into the last
  // visible slot so its selected ring is never hidden while collapsed.
  function ensureSelectedVisible(wrap) {
    var visible = wrap.__vuVisible;
    if (!visible) return;
    var swatches = Array.prototype.slice.call(wrap.querySelectorAll('.lru-v2__swatch'));
    var selIdx = -1;
    for (var i = 0; i < swatches.length; i++) {
      var inp = swatches[i].querySelector('[data-vu-input]');
      if (inp && inp.checked) {
        selIdx = i;
        break;
      }
    }
    if (selIdx < visible) return;
    wrap.insertBefore(swatches[selIdx], swatches[visible - 1]);
  }

  // Collapse to a single row: hide overflow swatches, show the chip, hide "less".
  function applyCollapsed(wrap) {
    var visible = wrap.__vuVisible;
    if (!visible) return;
    ensureSelectedVisible(wrap);
    var swatches = wrap.querySelectorAll('.lru-v2__swatch');
    swatches.forEach(function (s, i) {
      s.hidden = i >= visible;
    });
    wrap.classList.remove('lru-v2__swatches--expanded');
    var chip = wrap.querySelector('[data-vu-swatch-chip]');
    if (chip) {
      chip.hidden = false;
      chip.setAttribute('aria-expanded', 'false');
    }
    var less = wrap.parentElement && wrap.parentElement.querySelector('[data-vu-swatch-less]');
    if (less) less.hidden = true;
  }

  function expandSwatches(wrap) {
    wrap.querySelectorAll('.lru-v2__swatch').forEach(function (s) {
      s.hidden = false;
    });
    wrap.classList.add('lru-v2__swatches--expanded');
    var chip = wrap.querySelector('[data-vu-swatch-chip]');
    if (chip) {
      chip.hidden = true;
      chip.setAttribute('aria-expanded', 'true');
    }
    var less = wrap.parentElement && wrap.parentElement.querySelector('[data-vu-swatch-less]');
    if (less) less.hidden = false;
  }

  // Apply the overflow state to one wrap for the current width. When all swatches
  // fit, clear the chip and show them all; otherwise stamp "+N" and collapse.
  // Idempotent + reversible so it can run again on resize (narrow <-> wide).
  function applySwatchOverflow(wrap) {
    var swatches = wrap.querySelectorAll('.lru-v2__swatch');
    var visible = swatchVisibleCount(wrap);
    var chip = wrap.querySelector('[data-vu-swatch-chip]');
    var less = wrap.parentElement && wrap.parentElement.querySelector('[data-vu-swatch-less]');
    if (swatches.length <= visible) {
      // Everything fits — no chip; reveal all and drop any collapsed/expanded state.
      wrap.__vuVisible = 0;
      swatches.forEach(function (s) {
        s.hidden = false;
      });
      wrap.classList.remove('lru-v2__swatches--expanded');
      if (chip) chip.hidden = true;
      if (less) less.hidden = true;
      return;
    }
    wrap.__vuVisible = visible;
    if (chip) {
      var cnt = chip.querySelector('[data-vu-chip-count]');
      if (cnt) cnt.textContent = '+' + (swatches.length - visible);
    }
    applyCollapsed(wrap);
  }

  function setupSwatchOverflow(card) {
    card.querySelectorAll('[data-vu-swatch-wrap]').forEach(applySwatchOverflow);
  }

  // Recompute collapsed rows on viewport resize so the single row always fits the
  // current column width. Expanded wraps are left open (the shopper opened them).
  if (!window.__vuSwatchResizeBound) {
    window.__vuSwatchResizeBound = true;
    var vuResizeT;
    window.addEventListener('resize', function () {
      clearTimeout(vuResizeT);
      vuResizeT = setTimeout(function () {
        document.querySelectorAll('[data-vu-swatch-wrap]').forEach(function (wrap) {
          if (!wrap.classList.contains('lru-v2__swatches--expanded')) applySwatchOverflow(wrap);
        });
      }, 150);
    });
  }

  // Re-sync whenever the main product's variant changes. Registered once.
  // - V2 pickers dispatch `variant:change` (component-configuration-picker-v2.js).
  // - V1 pickers (variant-radios / variant-selects) fire a native `change` on
  //   their inputs; react to those, but ignore the upsell's own [data-vu-input]
  //   radios (which this file toggles) so there's no feedback loop.
  function syncMainToUpsells() {
    syncLockedToMain();
    syncColourToMain();
  }
  if (!window.__vuSizeSyncBound) {
    window.__vuSizeSyncBound = true;
    document.addEventListener('variant:change', syncMainToUpsells);
    document.addEventListener('change', function (event) {
      var t = event.target;
      if (!t || t.tagName !== 'SELECT' && t.type !== 'radio') return;
      if (t.closest('[data-vu-card]')) return;
      if (t.closest('variant-radios, variant-selects')) syncMainToUpsells();
    });
  }

  customElements.define(
    'variant-upsell-v2',
    class extends HTMLElement {
      connectedCallback() {
        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');
        // Deferred (bundle-into-main-ATC) mode when a V2 buy box is present;
        // otherwise fall back to immediate add-to-cart (e.g. V1 PDPs, drawer).
        this._deferred = !!document.querySelector('buy-buttons-v2');
        this._onChange = this._onChange.bind(this);
        this._onClick = this._onClick.bind(this);
        this.addEventListener('change', this._onChange);
        this.addEventListener('click', this._onClick);
        this.querySelectorAll('[data-vu-card]').forEach((c) => {
          this._resolve(c);
          // Collapse overflowing colour rows to a single row + "+N" chip.
          setupSwatchOverflow(c);
        });
        // Match this component's cards to the main product's size / arm type, then
        // pre-select each colour swatch to the main product's chosen colour.
        syncLockedToMain();
        syncColourToMain();
      }
      disconnectedCallback() {
        this.removeEventListener('change', this._onChange);
        this.removeEventListener('click', this._onClick);
      }
      _variants(card) {
        if (card.__vu) return card.__vu;
        var el = card.querySelector('[data-vu-variants]');
        try {
          card.__vu = JSON.parse(el.textContent);
        } catch (e) {
          card.__vu = [];
        }
        return card.__vu;
      }
      // Selected option value per position from the card's checked radios (null = unconstrained).
      _selected(card) {
        var sel = { 1: null, 2: null, 3: null };
        card.querySelectorAll('[data-vu-selector]').forEach(function (grp) {
          var pos = parseInt(grp.dataset.vuOption, 10);
          var checked = grp.querySelector('[data-vu-input]:checked');
          if (checked) sel[pos] = checked.value;
        });
        return sel;
      }
      _match(card) {
        var sel = this._selected(card);
        var mainVal = card.__vuMainVal || null; // set by Task 5 for match_main cards
        var matchOpt = card.dataset.vuMatchOption;
        return this._variants(card).find(function (v) {
          if (sel[1] != null && v.o1 !== sel[1]) return false;
          if (sel[2] != null && v.o2 !== sel[2]) return false;
          if (sel[3] != null && v.o3 !== sel[3]) return false;
          return true;
        });
      }
      // Editor discount amount (cents) for a base price, from the card's
      // data-vu-disc-type ('%' or '-') + data-vu-disc-value. Constraints mirror
      // the Liquid: value must be >= 1; percentage capped at 100; fixed (in
      // dollars) capped at the base price. Anything else = 0 (no discount).
      _discAmount(card, base) {
        var type = card.dataset.vuDiscType || '';
        var val = parseFloat(card.dataset.vuDiscValue) || 0;
        if (val < 1) return 0;
        if (type === '%') return Math.round((base * Math.min(val, 100)) / 100);
        if (type === '-') return Math.min(Math.round(val * 100), base);
        return 0;
      }
      // Editor-discounted prices for a variant: { base, sale, saving } in cents.
      // data-vu-disc-base 'compare' takes the discount off compare_at_price when
      // the variant has one above its price (else price), and clamps the sale so
      // it never exceeds the real price. saving = 0 means no editor discount.
      _discPrices(card, v) {
        var base = v.price;
        if (card.dataset.vuDiscBase === 'compare' && v.compare > v.price) base = v.compare;
        var amount = this._discAmount(card, base);
        if (amount <= 0) return { base: base, sale: v.price, saving: 0 };
        var sale = Math.min(base - amount, v.price);
        return { base: base, sale: sale, saving: base - sale };
      }
      // The price actually charged/shown for a variant after the editor discount.
      _salePrice(card, v) {
        return this._discPrices(card, v).sale;
      }
      // Paint every selector's selected tile/swatch + "Option: X" label from its
      // checked radio. Derived from DOM state (not the change event) because the
      // main-product sync can check a radio in a card whose <variant-upsell-v2>
      // isn't upgraded yet — e.g. after the PDP swaps in a re-rendered section,
      // the first block's connectedCallback syncs every card on the page — so no
      // change handler sees it; the card's own _resolve on connect fixes it up.
      _paintSelectors(card) {
        card.querySelectorAll('[data-vu-selector]').forEach(function (grp) {
          var input = grp.querySelector('[data-vu-input]:checked');
          if (!input) return;
          grp.querySelectorAll('.lru-v2__swatch, .lru-v2__tile').forEach(function (l) {
            var i = l.querySelector('[data-vu-input]');
            l.classList.toggle('lru-v2__swatch--selected', i === input && l.classList.contains('lru-v2__swatch'));
            l.classList.toggle('v2-tile--selected', i === input && l.classList.contains('lru-v2__tile'));
          });
          var lbl = grp.querySelector('[data-vu-optlabel]');
          if (lbl) lbl.textContent = input.value;
        });
      }
      _resolve(card) {
        this._paintSelectors(card);
        var v = this._match(card);
        if (!v) return;
        var fmt = this.dataset.moneyFormat;
        var disc = this._discPrices(card, v);
        var amount = disc.saving;
        var priceEl = card.querySelector('[data-vu-price]');
        if (priceEl) {
          var html;
          if (amount > 0) {
            // Editor discount: discounted sale over the struck base.
            html =
              '<span class="v2-price-sale">' +
              formatMoneyCents(disc.sale, fmt) +
              '</span><span class="v2-price-compare">' +
              formatMoneyCents(disc.base, fmt) +
              '</span>';
          } else if (v.compare > v.price) {
            // No editor discount but the variant has a native compare-at.
            html =
              '<span class="v2-price-sale">' +
              formatMoneyCents(v.price, fmt) +
              '</span><span class="v2-price-compare">' +
              formatMoneyCents(v.compare, fmt) +
              '</span>';
          } else {
            html =
              '<span class="v2-price-sale">' +
              formatMoneyCents(v.price, fmt) +
              '</span>';
          }
          priceEl.innerHTML = html;
        }
        var tagEl = card.querySelector('[data-vu-disc-tag]');
        if (tagEl) {
          if (amount > 0) {
            // Nothing left to pay reads "FREE", else "SAVE $X" (struck base minus sale).
            tagEl.textContent = disc.sale <= 0 ? 'FREE' : 'SAVE ' + formatMoneyCents(amount, fmt);
            tagEl.hidden = false;
          } else {
            tagEl.hidden = true;
          }
        }
        if (v.image) {
          var img = card.querySelector('[data-vu-image] img');
          if (img) {
            img.removeAttribute('srcset');
            img.removeAttribute('sizes');
            img.src = v.image;
          }
        }
        var add = card.querySelector('[data-vu-add]');
        if (add) add.dataset.variantId = v.id;
      }
      _onChange(event) {
        var input = event.target.closest('[data-vu-input]');
        if (!input || !this.contains(input)) return;
        var card = input.closest('[data-vu-card]');
        // Reconfiguring after adding = a different, un-added variant. Revert the
        // card to its ADD state so the stepper never controls the wrong line.
        if (card.__vuAddedId != null) this._exitAdded(card);
        // selected visual state + label are repainted by _resolve
        this._resolve(card);
        // Deferred: keep the reservation, re-pointing it at the newly selected
        // variant (and its price) so the main ATC total stays correct.
        if (card.__vuReserved) {
          var v = this._match(card);
          if (v) {
            card.__vuReserved.variantId = v.id;
            card.__vuReserved.priceCents = this._salePrice(card, v);
            dispatchUpsellChange();
          }
        }
      }
      _onClick(event) {
        // Colour overflow: chip expands the grid, "Show fewer" collapses it.
        var chip = event.target.closest('[data-vu-swatch-chip]');
        if (chip && this.contains(chip)) {
          expandSwatches(chip.closest('[data-vu-swatch-wrap]'));
          return;
        }
        var less = event.target.closest('[data-vu-swatch-less]');
        if (less && this.contains(less)) {
          var wrap = less.parentElement.querySelector('[data-vu-swatch-wrap]');
          if (wrap) applyCollapsed(wrap);
          return;
        }
        var step = event.target.closest('[data-vu-step]');
        if (step && this.contains(step) && !step.disabled) {
          var scard = step.closest('[data-vu-card]');
          if (scard) {
            var delta = parseInt(step.dataset.vuStep, 10);
            if (scard.__vuReserved) this._reserveQty(scard, delta);
            else this._changeQty(scard, delta);
          }
          return;
        }
        var button = event.target.closest('[data-vu-add]');
        if (!button || !this.contains(button) || button.disabled) return;
        var card = button.closest('[data-vu-card]');
        // Deferred + quantity stepper ON: reserve the item so it folds into the
        // main ATC. Otherwise (stepper OFF, or non-deferred pages) ADD is a plain
        // add-to-cart right now.
        if (this._deferred && this._qtyEnabled()) {
          this._reserve(card);
          return;
        }
        var id = button.dataset.variantId;
        if (id) this._addToCart(id, button, card);
      }
      // Quantity stepper is on unless the block's data-vu-qty-selector is "false".
      _qtyEnabled() {
        return this.dataset.vuQtySelector !== 'false';
      }
      // Display-only multiplier for variants sold as a pre-bundled set (e.g. the
      // Kin chair variant IS a "Set of 2"). The stepper shows the physical unit
      // count (qty * step) while the cart quantity, reserved qty and all price
      // math stay in variant units. Defaults to 1 (no multiplier).
      _displayStep(card) {
        return parseInt(card.dataset.vuQtyDisplayStep, 10) || 1;
      }
      _renderQty(card, qty) {
        var qtyEl = card.querySelector('[data-vu-qty]');
        if (qtyEl) qtyEl.textContent = qty * this._displayStep(card);
      }
      // --- Deferred reservation (bundle-into-main-ATC) ----------------------
      _showStepper(card, qty) {
        var add = card.querySelector('[data-vu-add]');
        var stepper = card.querySelector('[data-vu-stepper]');
        this._renderQty(card, qty);
        if (add) add.hidden = true;
        if (stepper) stepper.hidden = false;
      }
      _hideStepper(card) {
        var add = card.querySelector('[data-vu-add]');
        var stepper = card.querySelector('[data-vu-stepper]');
        if (add) add.hidden = false;
        if (stepper) stepper.hidden = true;
      }
      _reserve(card) {
        var v = this._match(card);
        if (!v) return;
        card.__vuReserved = { variantId: v.id, qty: 1, priceCents: this._salePrice(card, v) };
        this._showStepper(card, 1);
        dispatchUpsellChange();
      }
      _reserveQty(card, delta) {
        if (!card.__vuReserved) return;
        var next = card.__vuReserved.qty + delta;
        if (next < 1) {
          card.__vuReserved = null;
          this._hideStepper(card);
        } else {
          card.__vuReserved.qty = next;
          this._renderQty(card, next);
        }
        dispatchUpsellChange();
      }
      // Swap a card from its ADD button to the in-cart quantity stepper. Stores
      // the added variant id, the returned cart line key (for precise
      // /cart/change.js targeting), and the current quantity on the card.
      _enterAdded(card, variantId, lineKey, qty) {
        card.__vuAddedId = String(variantId);
        card.__vuLineKey = lineKey || null;
        card.__vuQty = qty || 1;
        var add = card.querySelector('[data-vu-add]');
        var stepper = card.querySelector('[data-vu-stepper]');
        this._renderQty(card, card.__vuQty);
        if (add) add.hidden = true;
        if (stepper) stepper.hidden = false;
      }
      _exitAdded(card) {
        card.__vuAddedId = null;
        card.__vuLineKey = null;
        card.__vuQty = 0;
        var add = card.querySelector('[data-vu-add]');
        var stepper = card.querySelector('[data-vu-stepper]');
        if (add) add.hidden = false;
        if (stepper) stepper.hidden = true;
      }
      // Stepper +/-: change the added line's quantity via /cart/change.js and
      // refresh the drawer. Dropping below 1 removes the line and reverts the
      // card to its ADD state.
      _changeQty(card, delta) {
        if (card.__vuAddedId == null) return;
        var nextQty = (card.__vuQty || 0) + delta;
        var removing = nextQty < 1;
        if (removing) nextQty = 0;
        var self = this;
        var stepper = card.querySelector('[data-vu-stepper]');
        if (stepper) stepper.classList.add('loading');
        var body = { id: card.__vuLineKey || parseInt(card.__vuAddedId, 10), quantity: nextQty };
        if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
          body.sections = this.cart.getSectionsToRender().map(function (s) {
            return s.id;
          });
          body.sections_url = window.location.pathname;
        }
        var config =
          typeof fetchConfig === 'function'
            ? fetchConfig('javascript')
            : {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Accept: 'application/javascript',
                },
              };
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify(body);
        fetch((window.routes && window.routes.cart_change_url) || '/cart/change.js', config)
          .then(function (r) {
            return r.json();
          })
          .then(function (resp) {
            if (resp && resp.status) {
              console.error(resp.description || resp.message);
              return;
            }
            if (removing) {
              self._exitAdded(card);
            } else {
              card.__vuQty = nextQty;
              self._renderQty(card, nextQty);
            }
            if (
              typeof publish === 'function' &&
              typeof PUB_SUB_EVENTS !== 'undefined'
            ) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'variant-upsell-v2',
                productVariantId: card.__vuAddedId,
              });
            }
            if (self.cart && typeof self.cart.renderContents === 'function')
              self.cart.renderContents(resp);
          })
          .catch(function (e) {
            console.error(e);
          })
          .finally(function () {
            if (stepper) stepper.classList.remove('loading');
          });
      }
      _addToCart(variantId, button, card) {
        var body = { items: [{ id: parseInt(variantId, 10), quantity: 1 }] };
        if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
          body.sections = this.cart.getSectionsToRender().map(function (s) {
            return s.id;
          });
          body.sections_url = window.location.pathname;
          if (typeof this.cart.setActiveElement === 'function')
            this.cart.setActiveElement(document.activeElement);
        }
        button.classList.add('loading');
        button.disabled = true;
        var config =
          typeof fetchConfig === 'function'
            ? fetchConfig('javascript')
            : {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Accept: 'application/javascript',
                },
              };
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify(body);
        var self = this;
        fetch(window.routes.cart_add_url, config)
          .then(function (r) {
            return r.json();
          })
          .then(function (resp) {
            if (resp.status) {
              console.error(resp.description || resp.message);
              return;
            }
            // Enter the Added state: swap ADD -> quantity stepper for this card,
            // keeping the returned line key for later /cart/change.js calls.
            var line = resp && resp.items && resp.items[0];
            // Show the in-cart quantity stepper only when the quantity selector
            // is enabled; when it's off, ADD is a plain add-to-cart (button stays
            // ADD so the shopper can add again).
            if (card && self._qtyEnabled()) self._enterAdded(card, variantId, line && line.key, 1);
            if (
              typeof publish === 'function' &&
              typeof PUB_SUB_EVENTS !== 'undefined'
            ) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'variant-upsell-v2',
                productVariantId: variantId,
              });
            }
            if (self.cart && typeof self.cart.renderContents === 'function')
              self.cart.renderContents(resp);
            else window.location = window.routes.cart_url;
          })
          .catch(function (e) {
            console.error(e);
          })
          .finally(function () {
            button.classList.remove('loading');
            button.disabled = false;
          });
      }
    },
  );
})();
